import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CFormTextarea,
  CSpinner,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheck,
  faCheckDouble,
  faPaperPlane,
  faPaperclip,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import WeAllTeamTabs from "@/components/weAllTeam/WeAllTeamTabs";
import WeAllTeamAvatar from "@/components/weAllTeam/WeAllTeamAvatar";
import WeAllTeamMessageBody from "@/components/weAllTeam/WeAllTeamMessageBody";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  MESSAGE_MAX_LENGTH,
  formatChatTime,
  toBool,
  toNumber,
} from "@/components/weAllTeam/weAllTeamUtils";
import WeAllTeamService from "@/api/weAllTeamService";
import { useTranslationContext } from "@/context/TranslationContext";
import { weAllTeam as tr } from "@/constants/translation/weAllTeam.translation";
import { weAllTeam as routes } from "@/libs/routes";
import {
  useReplyWeAllTeam,
  useWeAllTeamConversation,
} from "@/hooks/useWeAllTeam";

// Within this distance of the bottom a new message keeps the view pinned to the newest message
const STICK_TO_BOTTOM_PX = 120;

const StatusTick = ({ message, t }) => {
  const seen = toBool(message.is_seen);
  const delivered = toBool(message.is_delivered);
  const label = seen
    ? t(tr.status_seen)
    : delivered
      ? t(tr.status_delivered)
      : t(tr.status_sent);
  return (
    <span title={label} className={`weall-team-tick ${seen ? "seen" : ""}`}>
      <FontAwesomeIcon icon={delivered || seen ? faCheckDouble : faCheck} />
    </span>
  );
};

/**
 * One WeAll Team conversation: the full history (newest page live over the socket, older pages on demand) and a
 * composer to reply. Opening it marks the user's messages read on the server, which clears the unread badge on
 * every open admin tab.
 */
const WeAllTeamConversation = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id: conversationId } = useParams();

  const [reply, setReply] = useState("");
  const [file, setFile] = useState(null);
  const fileInput = useRef(null);
  const [older, setOlder] = useState({
    messages: [],
    nextPage: 2,
    hasMore: null,
    loading: false,
  });
  const listRef = useRef(null);
  const stickToBottom = useRef(true);
  const previousScrollHeight = useRef(null);

  // Start fresh when another conversation is opened
  useEffect(() => {
    setOlder({ messages: [], nextPage: 2, hasMore: null, loading: false });
    setReply("");
    setFile(null);
    stickToBottom.current = true;
  }, [conversationId]);

  const {
    data: response,
    isLoading,
    isError,
  } = useWeAllTeamConversation(conversationId, 1);
  const data = response?.data;
  const user = data?.user;
  const userDeleted = toBool(user?.is_deleted);
  const mediaCounts = {
    image: toNumber(data?.media_counts?.image),
    video: toNumber(data?.media_counts?.video),
    document: toNumber(data?.media_counts?.document),
  };
  const mediaTotal = toNumber(data?.media_counts?.total);

  const hasMoreOlder =
    older.hasMore === null
      ? toBool(data?.pagination?.hasMorePages)
      : older.hasMore;

  // History oldest -> newest: live page 1 plus whatever older pages were loaded, without duplicates
  const messages = useMemo(() => {
    const byId = new Map();
    [...(older.messages || []), ...(data?.messages || [])].forEach((message) =>
      byId.set(String(message.message_id), message),
    );
    // Ids are positive integers sent as strings: longer = larger, equal length compares as text
    const compareIds = (a, b) =>
      a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
    return [...byId.values()].sort((a, b) =>
      compareIds(String(a.message_id), String(b.message_id)),
    );
  }, [data, older.messages]);

  const loadOlder = useCallback(async () => {
    setOlder((prev) => ({ ...prev, loading: true }));
    previousScrollHeight.current = listRef.current?.scrollHeight ?? null;
    try {
      const page = await WeAllTeamService.getConversation({
        conversation_id: conversationId,
        page: older.nextPage,
      });
      setOlder((prev) => ({
        messages: [...(page?.data?.messages || []), ...prev.messages],
        nextPage: prev.nextPage + 1,
        hasMore: toBool(page?.data?.pagination?.hasMorePages),
        loading: false,
      }));
    } catch (error) {
      previousScrollHeight.current = null;
      setOlder((prev) => ({ ...prev, loading: false }));
      toast.error(error?.response?.data?.message || t(tr.toast_failed));
    }
  }, [conversationId, older.nextPage, t]);

  // Keep the scroll position: pinned to the newest message, or unchanged after prepending older ones
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (previousScrollHeight.current !== null) {
      el.scrollTop = el.scrollHeight - previousScrollHeight.current;
      previousScrollHeight.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    stickToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
  };

  const { mutate: sendReply, isPending } = useReplyWeAllTeam(
    () => {
      setReply("");
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      stickToBottom.current = true;
    },
    (error) =>
      toast.error(error?.response?.data?.message || t(tr.toast_failed)),
  );

  const handleSend = () => {
    const text = reply.trim();
    if ((!text && !file) || isPending || userDeleted) return;
    sendReply({ conversation_id: conversationId, message: text, file });
  };

  const handleFilePicked = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > ATTACHMENT_MAX_BYTES) {
      toast.error(t(tr.file_too_large));
      e.target.value = "";
      return;
    }
    setFile(picked);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="weall-team-conversation">
        <WeAllTeamTabs />
        <div className="text-center py-5">
          <CSpinner />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="weall-team-conversation">
        <WeAllTeamTabs />
        <CCard>
          <CCardBody className="text-center py-5">
            <p>{t(tr.not_found)}</p>
            <CButton color="primary" onClick={() => navigate(routes.inbox)}>
              {t(tr.back_to_inbox)}
            </CButton>
          </CCardBody>
        </CCard>
      </div>
    );
  }

  return (
    <div className="weall-team-conversation">
      <WeAllTeamTabs />
      <CCard className="weall-team-chat-card">
        <div className="weall-team-chat-header d-flex align-items-center gap-3">
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            className="weall-team-back"
            onClick={() => navigate(routes.inbox)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            {t(tr.back_to_inbox)}
          </CButton>
          <WeAllTeamAvatar src={user?.profile_photo_url} size={40} />
          <div>
            <div className="fw-bold">{user?.full_name}</div>
            {user?.user_name && (
              <small className="text-muted">@{user.user_name}</small>
            )}
          </div>
          {mediaTotal > 0 && (
            <div className="weall-team-media-summary ms-auto">
              <strong>{t(tr.media_summary)}: {mediaTotal}</strong>
              <small>
                {[
                  [mediaCounts.image, tr.media_photos],
                  [mediaCounts.video, tr.media_videos],
                  [mediaCounts.document, tr.media_documents],
                ]
                  .filter(([count]) => count > 0)
                  .map(([count, label]) => `${count} ${t(label)}`)
                  .join(" · ")}
              </small>
            </div>
          )}
        </div>

        <div
          className="weall-team-chat-body"
          ref={listRef}
          onScroll={handleScroll}
        >
          {hasMoreOlder && (
            <div className="text-center mb-3">
              <CButton
                color="secondary"
                variant="outline"
                size="sm"
                disabled={older.loading}
                onClick={loadOlder}
              >
                {older.loading ? t(tr.loading_older) : t(tr.load_older)}
              </CButton>
            </div>
          )}

          {messages.length === 0 && (
            <div className="text-center text-muted py-5">
              {t(tr.no_messages)}
            </div>
          )}

          {messages.map((message) => {
            const fromTeam = toBool(message.is_from_admin);
            return (
              <div
                key={message.message_id}
                className={`weall-team-message-row ${fromTeam ? "from-team" : "from-user"}`}
              >
                <div className="weall-team-bubble">
                  {fromTeam && (
                    <div className="weall-team-bubble-sender">
                      {t(tr.sender_team)}
                    </div>
                  )}
                  <WeAllTeamMessageBody message={message} />
                  <div className="weall-team-bubble-meta">
                    <span>{formatChatTime(message.created_at)}</span>
                    {fromTeam && <StatusTick message={message} t={t} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="weall-team-chat-composer">
          {userDeleted ? (
            <div className="text-muted small">{t(tr.deleted_user_notice)}</div>
          ) : (
            <>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept={ATTACHMENT_ACCEPT}
                onChange={handleFilePicked}
              />
              <CButton
                color="secondary"
                variant="outline"
                className="weall-team-attach"
                title={t(tr.attach_file)}
                disabled={isPending}
                onClick={() => fileInput.current?.click()}
              >
                <FontAwesomeIcon icon={faPaperclip} />
              </CButton>
              <div className="weall-team-composer-input">
                {file && (
                  <div className="weall-team-file-chip">
                    <FontAwesomeIcon icon={faPaperclip} />
                    <span className="weall-team-file-chip-name">{file.name}</span>
                    <button
                      type="button"
                      title={t(tr.remove_file)}
                      onClick={clearFile}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                )}
              <CFormTextarea
                rows={2}
                value={reply}
                maxLength={MESSAGE_MAX_LENGTH}
                placeholder={t(tr.reply_placeholder)}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              </div>
              <CButton
                color="primary"
                className="weall-team-send"
                disabled={isPending || (!reply.trim() && !file)}
                onClick={handleSend}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                {isPending ? t(tr.sending) : t(tr.send)}
              </CButton>
            </>
          )}
        </div>
      </CCard>
    </div>
  );
};

export default WeAllTeamConversation;
